import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  AgentAsyncResponse,
  AgentChatService,
  AgentConversation,
  AgentMessage,
  AgentStreamError,
  AgentTaskStatus,
  AgentUsage,
  SSEEvent,
} from './agent-chat.service';
import { SocketService } from 'src/app/shared/services/socket.service';

export type ChatMsgRole = 'user' | 'assistant' | 'tool' | 'thinking' | 'error';

export interface ChatMsg {
  role: ChatMsgRole;
  content: string;
  toolName?: string | null;
  timestamp?: Date;
  usage?: AgentUsage;
  rateLimited?: boolean;
  retryAfterSeconds?: number;
}

const TASK_POLL_INTERVAL_MS = 3000;
const TASK_POLL_MAX_ATTEMPTS = 120; // ~6 min total

/**
 * Controller con estado para chat del agente. Debe proveerse por componente
 * (`providers: [AgentChatController]`) para que widget y panel tengan instancias
 * independientes compartiendo la lógica.
 */
@Injectable()
export class AgentChatController implements OnDestroy {
  // ---- State ----
  conversations: AgentConversation[] = [];
  currentConversation: AgentConversation | null = null;
  messages: ChatMsg[] = [];
  isStreaming = false;
  isLoading = false;
  streamingText = '';
  thinkingText = '';
  currentToolName = '';
  lastRateLimitRetryAfter: number | null = null;

  // Callback opcional para que el componente haga scroll o re-render
  onStateChange?: () => void;

  private abortController: AbortController | null = null;
  private socketSub: Subscription | null = null;
  private taskPollTimer: any = null;

  constructor(
    private agentService: AgentChatService,
    private socketService: SocketService,
  ) {}

  /** Suscribe a notificaciones de async task completion vía WebSocket. */
  init(): void {
    this.socketSub?.unsubscribe();
    this.socketSub = this.socketService.onAgentTaskComplete$().subscribe((data: any) => {
      if (this.currentConversation && data?.conversationId === this.currentConversation.id) {
        this.stopTaskPolling();
        this.selectConversation(this.currentConversation, { silent: true });
      }
    });
  }

  ngOnDestroy(): void {
    this.socketSub?.unsubscribe();
    this.abortController?.abort();
    this.stopTaskPolling();
  }

  // ---- Conversations ----

  async loadConversations(page = 1, limit = 20): Promise<void> {
    try {
      const result = await this.agentService.listConversations(page, limit);
      this.conversations = result.data;
      this.notify();
    } catch {}
  }

  async newConversation(): Promise<AgentConversation | null> {
    try {
      const conv = await this.agentService.createConversation();
      this.conversations.unshift(conv);
      await this.selectConversation(conv);
      return conv;
    } catch {
      return null;
    }
  }

  async selectConversation(
    conv: AgentConversation,
    opts: { silent?: boolean } = {},
  ): Promise<void> {
    this.currentConversation = conv;
    if (!opts.silent) this.messages = [];
    this.isLoading = true;
    this.notify();

    try {
      const result = await this.agentService.getConversation(conv.id);
      this.currentConversation = result.conversation;
      this.messages = this.mapMessages(result.messages);
    } catch {
    } finally {
      this.isLoading = false;
      this.notify();
    }
  }

  async deleteConversation(conv: AgentConversation): Promise<void> {
    try {
      await this.agentService.archiveConversation(conv.id);
      this.conversations = this.conversations.filter((c) => c.id !== conv.id);
      if (this.currentConversation?.id === conv.id) {
        this.currentConversation = null;
        this.messages = [];
      }
      this.notify();
    } catch {}
  }

  // ---- Send (streaming) ----

  async sendMessage(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed || this.isStreaming) return;

    if (!this.currentConversation) {
      await this.newConversation();
    }
    if (!this.currentConversation) return;

    this.pushUser(trimmed);
    this.resetStreamingBuffers();
    this.isStreaming = true;
    this.lastRateLimitRetryAfter = null;
    this.notify();

    this.abortController = await this.agentService.sendMessageStreaming(
      this.currentConversation.id,
      trimmed,
      {
        onEvent: (ev) => this.handleSSEEvent(ev),
        onDone: () => this.handleStreamDone(),
        onError: (err) => this.handleStreamError(err),
      },
    );
  }

  /** Envía en modo async (extended thinking). UI debe esperar el socket o polling. */
  async sendMessageAsyncMode(text: string): Promise<AgentAsyncResponse | null> {
    const trimmed = text.trim();
    if (!trimmed || this.isStreaming) return null;

    if (!this.currentConversation) {
      await this.newConversation();
    }
    if (!this.currentConversation) return null;

    this.pushUser(trimmed);
    this.resetStreamingBuffers();
    this.isStreaming = true;
    this.currentToolName = 'deep_thinking';
    this.notify();

    try {
      const resp = await this.agentService.sendMessageAsync(
        this.currentConversation.id,
        trimmed,
      );

      if (resp.duplicate) {
        this.isStreaming = false;
        this.pushError('Mensaje ya procesado (idempotency).');
        return resp;
      }

      if (resp.taskId) {
        this.startTaskPolling(this.currentConversation.id, resp.taskId);
      }
      return resp;
    } catch (err: any) {
      this.isStreaming = false;
      this.pushError(err?.error?.message || err?.message || 'Error en análisis async');
      this.notify();
      return null;
    }
  }

  stopStreaming(): void {
    this.abortController?.abort();
    this.stopTaskPolling();
    if (this.streamingText) {
      this.messages.push({
        role: 'assistant',
        content: this.streamingText + '\n\n*[Respuesta interrumpida]*',
        timestamp: new Date(),
      });
    }
    this.resetStreamingBuffers();
    this.isStreaming = false;
    this.notify();
  }

  // ---- SSE event handling ----

  private handleSSEEvent(event: SSEEvent): void {
    switch (event.type) {
      case 'text_delta':
        this.streamingText += event.text || '';
        break;

      case 'thinking':
        this.thinkingText += event.text || '';
        break;

      case 'tool_use':
        this.currentToolName = event.name || '';
        this.messages.push({
          role: 'tool',
          content: this.agentService.formatToolName(event.name),
          toolName: event.name,
          timestamp: new Date(),
        });
        break;

      case 'tool_executing':
        this.currentToolName = event.name || '';
        break;

      case 'tool_result':
        this.currentToolName = '';
        break;

      case 'message_complete':
        if (this.streamingText) {
          this.messages.push({
            role: 'assistant',
            content: this.streamingText,
            timestamp: new Date(),
            usage: event.usage,
          });
          this.streamingText = '';
        } else if (event.usage && this.messages.length) {
          // Stamp usage en el último assistant message si no hay nuevo texto
          const last = [...this.messages].reverse().find((m) => m.role === 'assistant');
          if (last && !last.usage) last.usage = event.usage;
        }
        break;

      case 'error':
        this.messages.push({
          role: 'error',
          content: event.message || 'Error',
          timestamp: new Date(),
        });
        break;
    }
    this.notify();
  }

  private handleStreamDone(): void {
    if (this.streamingText) {
      this.messages.push({
        role: 'assistant',
        content: this.streamingText,
        timestamp: new Date(),
      });
      this.streamingText = '';
    }
    this.isStreaming = false;
    this.thinkingText = '';
    this.currentToolName = '';
    // Actualiza messageCount local sin re-fetch de toda la lista
    if (this.currentConversation) {
      this.currentConversation.messageCount += 2;
      this.syncConversationInList(this.currentConversation);
    }
    this.notify();
  }

  private handleStreamError(error: AgentStreamError): void {
    this.messages.push({
      role: 'error',
      content: error.rateLimited && error.retryAfterSeconds
        ? `${error.message} (reintenta en ${error.retryAfterSeconds}s)`
        : error.message,
      timestamp: new Date(),
      rateLimited: error.rateLimited,
      retryAfterSeconds: error.retryAfterSeconds,
    });
    this.lastRateLimitRetryAfter = error.retryAfterSeconds ?? null;
    this.isStreaming = false;
    this.streamingText = '';
    this.thinkingText = '';
    this.currentToolName = '';
    this.notify();
  }

  // ---- Async task polling (fallback al socket) ----

  private startTaskPolling(conversationId: string, taskId: string): void {
    this.stopTaskPolling();
    let attempts = 0;

    const tick = async () => {
      attempts++;
      try {
        const status = await this.agentService.getTaskStatus(conversationId, taskId);
        if (status.found && (status.status === 'completed' || status.status === 'error')) {
          this.stopTaskPolling();
          this.isStreaming = false;
          this.currentToolName = '';
          if (status.status === 'error') {
            this.pushError(status.errorMessage || 'Error en análisis');
          }
          if (this.currentConversation?.id === conversationId) {
            await this.selectConversation(this.currentConversation, { silent: true });
          }
          return;
        }
      } catch {}

      if (attempts >= TASK_POLL_MAX_ATTEMPTS) {
        this.stopTaskPolling();
        this.isStreaming = false;
        this.currentToolName = '';
        this.pushError('Timeout esperando resultado');
        return;
      }
    };

    this.taskPollTimer = setInterval(tick, TASK_POLL_INTERVAL_MS);
  }

  private stopTaskPolling(): void {
    if (this.taskPollTimer) {
      clearInterval(this.taskPollTimer);
      this.taskPollTimer = null;
    }
  }

  // ---- Utils ----

  formatToolName(name: string | null | undefined): string {
    return this.agentService.formatToolName(name);
  }

  private pushUser(content: string): void {
    this.messages.push({ role: 'user', content, timestamp: new Date() });
  }

  private pushError(content: string): void {
    this.messages.push({ role: 'error', content, timestamp: new Date() });
  }

  private resetStreamingBuffers(): void {
    this.streamingText = '';
    this.thinkingText = '';
    this.currentToolName = '';
  }

  private syncConversationInList(conv: AgentConversation): void {
    const idx = this.conversations.findIndex((c) => c.id === conv.id);
    if (idx !== -1) this.conversations[idx] = { ...conv };
  }

  private mapMessages(msgs: AgentMessage[]): ChatMsg[] {
    const result: ChatMsg[] = [];
    for (const m of msgs) {
      if (m.role === 'user') {
        result.push({ role: 'user', content: m.content, timestamp: new Date(m.createdAt) });
      } else if (m.role === 'assistant') {
        const usage =
          m.inputTokens != null && m.outputTokens != null
            ? ({
                inputTokens: m.inputTokens,
                outputTokens: m.outputTokens,
                cacheReadTokens: 0,
                estimatedCostUsd: 0,
              } as AgentUsage)
            : undefined;
        result.push({
          role: 'assistant',
          content: m.content,
          timestamp: new Date(m.createdAt),
          usage,
        });
      } else if (m.role === 'tool_use') {
        result.push({
          role: 'tool',
          content: this.agentService.formatToolName(m.toolName),
          toolName: m.toolName,
          timestamp: new Date(m.createdAt),
        });
      }
    }
    return result;
  }

  private notify(): void {
    this.onStateChange?.();
  }
}
